import { NIGHT_STEPS, ROLE_IDS, ROLES, type RoleId, TABLE_STEPS } from '@onuw/roles-data'
import { Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { RoleLink } from '../RichText'
import styles from './guides.module.css'
import { R } from './parts'

/** What happens at each step, in a line. A step without a note shows its role's card text. */
const NOTES: Record<string, string> = {
  'oracle.question': 'Answers a question the whole table hears, and may change sides by the answer',
  'copycat.copy': 'Looks at a center card and becomes that role',
  'doppelganger.copy': 'Looks at a player’s card and becomes that role (at dusk when marks are in play)',
  'vampires.wake': 'The Vampires see each other and give a player the Mark of the Vampire',
  'count.fear': 'Gives a player the Mark of Fear: no night action for them',
  'renfield.wake': 'Sees the Vampires and the player they marked; takes the Mark of the Bat',
  'diseased.mark': 'Gives a neighbour the Mark of Disease',
  'cupid.love': 'May give two players a Mark of Love',
  'instigator.mark': 'May give a player the Mark of the Traitor',
  'priest.cleanse': 'Takes a Mark of Clarity and may give another, wiping the mark under it',
  'assassin.mark': 'Gives a player the Mark of the Assassin',
  'appAssassin.wake': 'Sees the Assassin, or gives the mark herself when there is none',
  'marks.view': 'Everyone looks at their own mark',
  'lovers.wake': 'The players with a Mark of Love see each other',
  'sentinel.shield': 'May shield another player’s card for the rest of the night',
  'alien.wake': 'The Aliens see each other and carry out the action the app announces',
  'cow.wake': 'Learns whether a neighbour is an Alien',
  'groobZerb.wake': 'Groob and Zerb look for each other',
  'bodySnatcher.snatch': 'May swap his card as the app instructs; the card he takes becomes an Alien card',
  'wolves.wake': 'The Werewolves see each other; a lone wolf may look at one center card',
  'alphaWolf.swap': 'Swaps the Center Werewolf card with a player’s card',
  'mysticWolf.view': 'May look at one player’s card',
  'minion.wake': 'Sees who the Werewolves are',
  'appTanner.wake': 'Sees who the Tanner is',
  'leader.wake': 'Sees who the Aliens are',
  'mason.wake': 'The Masons see each other',
  'thing.touch': 'May tap a neighbour',
  'seer.view': 'May look at one player’s card or two center cards',
  'appSeer.view': 'May look at one center card',
  'paranormalInvestigator.view':
    'May look at up to two players’ cards, and becomes the first evil one he sees',
  'marksman.view': 'May look at one player’s card and another player’s mark',
  'nostradamus.view': 'May look at up to three players’ cards; the table hears which team he ends on',
  'psychic.view': 'Looks at cards as the app instructs',
  'robber.rob': 'May swap cards with a player, and looks at the new card',
  'witch.brew': 'May look at a center card, and must then give it to a player',
  'pickpocket.steal': 'May swap marks with a player, and looks at the new mark',
  'troublemaker.swap': 'May swap two other players’ cards',
  'villageIdiot.move': 'May shift every other player’s card one seat left or right',
  'auraSeer.wake': 'Sees who has looked at a card or moved one so far',
  'gremlin.swap': 'May swap two players’ cards, or their marks',
  'rascal.act': 'Moves cards as the app instructs',
  'drunk.swap': 'Swaps their card with a center card, unseen',
  'insomniac.wake': 'Looks at her own card',
  'squire.wake': 'Sees who holds the werewolf cards now',
  'beholder.wake': 'Sees who holds the Seer cards now',
  'revealer.reveal': 'May turn a player’s card face up',
  'exposer.expose': 'Turns one to three center cards face up',
  'empath.point': 'Everyone points at a player, eyes closed',
  'empath.watch': 'Sees who pointed at whom',
  'curator.artifact': 'May give a player a random artifact',
  'blob.wake': 'Learns which neighbours are part of the Blob',
  'mortician.view': 'Looks at a neighbour’s card as the app instructs',
}

const rolesOfStep = (stepId: string): RoleId[] =>
  ROLE_IDS.filter((id) => ROLES[id].night?.some((s) => s.id === stepId))

const SLEEPERS = ROLE_IDS.filter((id) => !ROLES[id].night?.length)

export function NightOrder() {
  const { t } = useTranslation()
  // the table's own steps (everyone looks at their mark, the lovers, pointing for the Empath) sit among the roles'
  const steps = [...NIGHT_STEPS, ...TABLE_STEPS].sort((a, b) => a.order - b.order)
  const firstNight = steps.find((s) => s.order >= 0.2)
  return (
    <>
      <h2>Why the order matters</h2>
      <p>
        Cards move during the night. The <R id="seer" /> looks before the <R id="robber" /> steals, the Robber
        steals before the <R id="troublemaker" /> swaps, and the <R id="drunk" /> trades with the center after
        all of them. So a look tells you what a card was at that moment, not what the player holds in the
        morning. The roles are numbered in the order they wake, and the number is printed small on each card
        of the <Link to="/roles/">roles page</Link>.
      </p>
      <p>
        Take a table with a Seer, a Robber, a Troublemaker and an <R id="insomniac" />. The Seer sees that Ann
        is a Werewolf. The Robber then robs Ann and becomes the Werewolf. The Troublemaker swaps the Robber’s
        card with Ben’s. In the morning the Seer truthfully says "Ann was a Werewolf", yet the wolf card lies
        in front of Ben, and only the Insomniac, who looked last, knows for certain what she holds.
      </p>

      <h2>Dusk comes first</h2>
      <p>
        With a role of the <Link to="/guides/vampire/">Vampire pack</Link> in the deck, the night is preceded
        by dusk. Everyone starts with a face-down Mark of Clarity, and the dusk roles hand out other marks.
        When dusk ends, everyone looks at their own mark and the players with a Mark of Love see each other.
        The marks are described in <Link to="/guides/marks-and-artifacts/">Marks and artifacts</Link>.
      </p>

      <h2>The full order</h2>
      <p>
        Every step of a night with all {ROLE_IDS.length} roles. A real game has only the steps of the roles in
        its deck. The unnumbered rows are steps the whole table takes.
      </p>
      <div className={styles.scroll}>
        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>Who wakes</th>
              <th>What happens</th>
            </tr>
          </thead>
          <tbody>
            {steps.map((step) => {
              const roles = rolesOfStep(step.id)
              const place = NIGHT_STEPS.findIndex((s) => s.id === step.id) + 1
              return (
                <Fragment key={step.id}>
                  {step === steps[0] && (
                    <tr className={styles.phase}>
                      <td colSpan={3}>Before dusk</td>
                    </tr>
                  )}
                  {step.id === 'vampires.wake' && (
                    <tr className={styles.phase}>
                      <td colSpan={3}>Dusk (only with marks in play)</td>
                    </tr>
                  )}
                  {step === firstNight && (
                    <tr className={styles.phase}>
                      <td colSpan={3}>Night</td>
                    </tr>
                  )}
                  <tr>
                    <td className={styles.num}>{place > 0 ? place : ''}</td>
                    <td>
                      {roles.length === 0
                        ? 'Everyone'
                        : roles.map((r, i) => (
                            <Fragment key={r}>
                              {i > 0 && ', '}
                              <RoleLink id={r} />
                            </Fragment>
                          ))}
                    </td>
                    <td>{NOTES[step.id] ?? (roles[0] ? t(`roles:${roles[0]}.description`) : '')}</td>
                  </tr>
                </Fragment>
              )
            })}
          </tbody>
        </table>
      </div>

      <h2>Roles that never wake</h2>
      <p>
        {SLEEPERS.map((r, i) => (
          <Fragment key={r}>
            {i > 0 && ', '}
            <RoleLink id={r} />
          </Fragment>
        ))}
        . Their powers work in the vote, or not at all. Any of them may still wake up as something else: what
        counts is the card in front of you when the night ends.
      </p>

      <h2>Acting at the same time</h2>
      <p>
        At a physical table a narrator calls the roles one after another. Here every player has their own
        screen, so roles that cannot affect each other act at the same moment: the Seer need not wait for the{' '}
        <R id="mason">Masons</R>. Each player sees the table as it was when their group began, plus their own
        changes, and the card moves are then applied in wake order. The result is exactly what calling the
        roles one by one would give, in a fraction of the time.
      </p>
      <p>
        Timing gives nothing away either. When a role’s card lies in the center, the night still pauses for
        it, for a few seconds of random length. Nobody can tell from the rhythm of the night whether a role is
        in play, who is awake, or who acted last.
      </p>

      <h2>Rules of thumb for the morning</h2>
      <ul>
        <li>A look is about the card, not about the player. Ask what moved after it.</li>
        <li>
          When two roles moved the same card, the later one decides where it lies. The Robber comes before the{' '}
          <R id="witch" />, the Witch before the Troublemaker, the Troublemaker before the{' '}
          <R id="villageIdiot" />, the <R id="gremlin" /> and the Drunk.
        </li>
        <li>
          The Insomniac, the <R id="squire" />, the <R id="beholder" />, the <R id="revealer" />, the{' '}
          <R id="exposer" /> and the <R id="mortician" /> act after every move. What they saw is how the night
          ended.
        </li>
        <li>
          The <R id="sentinel" /> acts before nearly all of them: from then on a shielded card can be neither
          looked at nor moved.
        </li>
      </ul>
    </>
  )
}
